import { Inject, Injectable, NgZone, DOCUMENT } from '@angular/core';

import { SwgeTheme } from './themes/swge';
import { THEMES } from './parallax-background.constants';
import { WindowRef } from '@finder/shared/services/window-ref/window-ref.service';
import { ParallaxThemeInterface } from './themes/parallax-theme.inferface';
import { DefaultTheme } from './themes/default';
import { CreateStyleProvider, CREATE_STYLE } from '@finder/shared/utils/dom/createStyle';
import { DebugLogService } from '@finder/shared/services/debug-log/debug-log.service';
import { fromEvent } from 'rxjs';
import { CUSTOM_EVENTS } from '@finder/shared/constants/custom-events.constants';


const SERVICE_NAME = 'Parallax Background Service:';

@Injectable()
export class ParallaxBackgroundService {
    data: any;
    viewport = {
        bottom: 0,
        height: 0,
        delta: 0
    };

    private oldScroll = 0;
    private themeClass: ParallaxThemeInterface;

    constructor(
        private winRef: WindowRef,
        private zone: NgZone,
        private debugLogger: DebugLogService,
        @Inject(DOCUMENT) private document: Document,
        @Inject(CREATE_STYLE) private createStyleUtil: CreateStyleProvider,
    ) { }

    /**
     * Create a theme for the page
     */
    createTheme(theme: string, element) {
        switch (theme) {
            case THEMES.SWGE:
                this.themeClass = new SwgeTheme(element);
                break;
            default:
                this.debugLogger.log(`${SERVICE_NAME} Default theme applied`);
                this.themeClass = new DefaultTheme(element);
                break;
        }

        // The service is passed in later to break circular reference errors
        this.themeClass.setup(this);

        // Watch for custom events
        fromEvent(this.document, CUSTOM_EVENTS.TRIGGER_METHOD)
            .subscribe((event: CustomEvent) => {
                // check for method before triggering it
                if (!this.themeClass[event.detail.method]) {
                    return;
                }

                // trigger the method
                this.themeClass[event.detail.method]();
            });
    }

    /**
     * Use this to add a new style tag to the header
     */
    createStyle(css: string, id?: string) {
        this.createStyleUtil(this.document, css, id);
    }

    handleIntersection(ioEntry: IntersectionObserverEntry) {
        this.themeClass.handleIntersection(ioEntry);
    }

    updateViewport() {
        // Check to see what direction we're scrolling
        if (this.oldScroll < this.winRef.nativeWindow.scrollY) {
            // Down
            this.viewport.delta = -1;
        }
        if (this.oldScroll > this.winRef.nativeWindow.scrollY) {
            // Up
            this.viewport.delta = 1;
        }
        // Update position
        this.oldScroll = this.winRef.nativeWindow.scrollY;

        this.viewport.bottom = this.winRef.nativeWindow.scrollY + this.winRef.nativeWindow.innerHeight;
        this.viewport.height = this.winRef.nativeWindow.innerHeight;
    }

    // Start watching an element on the page
    startScrollWatch(element, backgroundLayer) {
        // Don't set start multiple watches
        if (element.watchStarted) {
            return;
        }

        // Save the function passed to the listener
        element.watchStarted = this.pinBackgroundToElm.bind(this, element, backgroundLayer);
        // Capture the event outside ng. This triggers change detection
        this.zone.runOutsideAngular(() => {
            this.winRef.nativeWindow.addEventListener('scroll', element.watchStarted);
        });
    }

    // Cancel the watch
    cancelScrollWatch(element) {
        this.winRef.nativeWindow.removeEventListener('scroll', element.watchStarted);
        // Clear the listener
        delete element.watchStarted;
    }

    transition(element) {
        const SCROLL_DOWN = 'scrollDown';
        const SCROLL_UP = 'scrollUp';

        // Figure out which animation we want to use
        const direction = this.viewport.delta === -1 ? SCROLL_DOWN : SCROLL_UP;

        const animation = element.dataset[direction];
        const tag = `${direction}:${animation}`;

        // Set the animation on the trigger element
        if (element.dataset.animation === tag) {
            // Don't run the same animation twice
            return;
        }

        element.dataset.animation = tag;

        return animation;
    }

    // Get distance from the top of the page
    private getElemDist(element) {
        /**
         * we can't use offsetTop. This only gives the distance to the next parent.
         * if it's nested then the value can be 0.
         *
         * getBoundingClientRect will return the distance to the top of the page
         * but if the scrollbar isn't at the top of the page we'll get the wrong
         * value. including the scrollbar position makes up for this
         * https://stackoverflow.com/a/33840267/122236
         */
        element.dataset.top = element.getBoundingClientRect().top + this.winRef.nativeWindow.scrollY;
    }

    private pinBackgroundToElm(element, backgroundLayer) {
        function update() {
            this.updateViewport();
            this.getElemDist(element);

            const distToTop = ((this.viewport.bottom - element.dataset.top) * 100) / this.viewport.height;

            backgroundLayer.style.top = `-${distToTop}vh`;
        }

        /**
         * Calculate the distance from the pin element to the top of the page
         * as a percentage. use this to adjust the pinned background top property.
         * Use requestAnimationFrame as an optimization
         * https://developer.mozilla.org/en-US/docs/Web/API/Document/scroll_event
         */
        this.winRef.nativeWindow.requestAnimationFrame(update.bind(this));
    }
}
